import {useMemo} from 'react';

import type {
  ImportPanelMenuEntityFragmentFragment,
  ImportPanelMenuProjectsQuery,
  ImportPanelMenuProjectsQueryVariables,
  ImportPanelMenuViewSpecQuery,
  ImportPanelMenuViewSpecQueryVariables,
  ImportPanelMenuViewsQuery,
  ImportPanelMenuViewsQueryVariables,
  ImportPanelProjectViewsFragmentFragment,
} from '../../generated/graphql';
import {useQuery} from '../../state/graphql/apolloWithAbort';
import {useViewer} from '../../state/viewer/hooks';
import type {Viewer} from '../../state/viewer/types';
import {useDecompressedMultiRunWorkspaceSpec} from '../../state/views/decompress';
import {type ProjectPageViewSpec} from '../../state/views/projectPage/types';
import {getPanelSpec, type LayedOutPanel} from '../../util/panelTypes';
import {
  IMPORT_PANEL_MENU_PROJECTS,
  IMPORT_PANEL_MENU_VIEW_SPEC,
  IMPORT_PANEL_MENU_VIEWS,
} from './importPanelMenuQueries';

type Project = {
  id: string;
  name: string;
};
type Team = {
  id: string;
  name: string;
  projects: readonly Project[];
};

function isValidTeam(value: unknown): value is Team {
  if (!value || typeof value !== 'object') {
    return false;
  }
  // @ts-expect-error -- we know the value is an object, ensure projects is an array
  const projects = value.projects;
  return !(!Array.isArray(projects) || projects.length === 0);
}

// Convert the GQL response to something that has some more guarantees about
// its shape. Get rid of edges, we don't need them to get work done.
function convertToLooseTeam(
  team: ImportPanelMenuEntityFragmentFragment | null | undefined
) {
  if (!team || !team.projects) {
    return null;
  }
  return {
    id: team.id,
    name: team.name,
    projects: team.projects.edges.map(projectEdge => {
      if (!projectEdge.node) {
        return null;
      }
      const projectNode = projectEdge.node;
      return {
        id: projectNode.id,
        name: projectNode.name,
      };
    }),
  };
}

type UseTeamsAndProjectsOpts = {
  selectedEntityName?: string;
  projectSearchQuery?: string;
};

/**
 * Retrieves a nested data structure of the user's entities (teams) and the
 * projects that each of those entities have. The user may type in a search
 * query to narrow down the list of projects.
 *
 * @param selectedEntityName
 * @param projectSearchQuery
 */
export function useTeamsAndProjects({
  selectedEntityName,
  projectSearchQuery,
}: UseTeamsAndProjectsOpts): readonly Team[] {
  const user = useViewer();
  if (!user || !user.username) {
    throw new RangeError(`Unexpected empty user or username`);
  }

  const {data, error} = useQuery<
    ImportPanelMenuProjectsQuery,
    ImportPanelMenuProjectsQueryVariables
  >(IMPORT_PANEL_MENU_PROJECTS, {
    variables: {
      userName: user.username,
      selectedEntityName,
      projectSearchQuery,
    },
  });

  if (error) {
    throw error;
  }

  return useMemo(() => {
    if (!data || !data.user || !data.user.teams) {
      return [];
    }

    const selectedTeam = convertToLooseTeam(data.entity);
    const mainListHasSelectedTeam = Boolean(
      data.user.teams.edges.find(teamEdge => {
        return (
          selectedTeam && teamEdge.node && teamEdge.node.id === selectedTeam.id
        );
      })
    );

    const teams = data.user.teams.edges
      .map(teamEdge => convertToLooseTeam(teamEdge.node))
      .filter(isValidTeam) as readonly Team[];

    return mainListHasSelectedTeam || !isValidTeam(selectedTeam)
      ? teams
      : [selectedTeam, ...teams];
  }, [data]);
}

function getViewIdForUser(
  views: readonly ImportPanelProjectViewsFragmentFragment[],
  user: Viewer
): string | null {
  if (views.length === 0) {
    return null;
  }
  const firstView = views[0]; // fallback in case we don't find anything

  const appropriateView = views.find(view => {
    return view.user && view.user.id === user.id;
  });

  return (appropriateView ?? firstView).id;
}

type PanelItem = {
  name: string;
  panel: LayedOutPanel;
};

type ViewSection = {
  sectionName: string;
  panels: readonly PanelItem[];
};

function getPanelsBySectionForViewspec(viewSpec: any): readonly ViewSection[] {
  const sections = viewSpec.section?.panelBankConfig?.sections ?? [];

  return sections
    .map((section: any) => {
      if (!section.panels || !section.panels.length) {
        return null;
      }
      const panelItemList: readonly PanelItem[] = section.panels
        .map((panel: LayedOutPanel) => {
          const panelSpec = getPanelSpec(panel.viewType);
          const panelName =
            panelSpec.getTitleFromConfig?.(panel.config as any) ??
            panelSpec.type;
          return {
            name: panelName,
            panel,
          };
        })
        .sort((p1: PanelItem, p2: PanelItem) => {
          return p1.name
            .toLocaleLowerCase()
            .localeCompare(p2.name.toLocaleLowerCase());
        });

      return {
        sectionName: section.name,
        panels: panelItemList,
      };
    })
    .filter(Boolean);
}

type UseViewspecForSelectedProjectOpts = {
  entityName?: string;
  projectName?: string;
};

/**
 * Given a project, returns the most-appropriate single viewspec for the current
 * user, entity, and project. Returns it in a structure that makes it easier to
 * build a menu out of the elements.
 *
 * @param entityName
 * @param projectName
 */
export function usePanelsForProject({
  entityName,
  projectName,
}: UseViewspecForSelectedProjectOpts): readonly ViewSection[] {
  const user = useViewer();
  if (!user || !user.username) {
    throw new RangeError(`Unexpected empty user or username`);
  }

  const {
    data: allViewsQuery,
    loading: isLoadingAllViews,
    error: allViewsError,
  } = useQuery<ImportPanelMenuViewsQuery, ImportPanelMenuViewsQueryVariables>(
    IMPORT_PANEL_MENU_VIEWS,
    {
      variables: {
        entityName,
        projectName,
        userName: user.username,
      },
    }
  );
  if (allViewsError) {
    throw allViewsError;
  }

  const viewId = useMemo(() => {
    if (
      !allViewsQuery ||
      !allViewsQuery.project ||
      !allViewsQuery.project.allViews
    ) {
      return null;
    }
    const views = allViewsQuery.project.allViews.edges
      .filter(e => Boolean(e.node))
      .map(e => e.node) as ImportPanelProjectViewsFragmentFragment[];
    return getViewIdForUser(views, user);
  }, [allViewsQuery, user]);

  const {
    data: viewData,
    loading: isLoadingView,
    error: viewError,
  } = useQuery<
    ImportPanelMenuViewSpecQuery,
    ImportPanelMenuViewSpecQueryVariables
  >(IMPORT_PANEL_MENU_VIEW_SPEC, {
    variables: {
      viewId: viewId as string, // can be null but we skip if that's the case
    },
    skip: !viewId,
  });
  if (viewError) {
    throw viewError;
  }

  const parsedSpec = useMemo(() => {
    if (isLoadingAllViews || isLoadingView || !viewData?.view?.spec) {
      return;
    }
    return JSON.parse(viewData.view.spec) as ProjectPageViewSpec;
  }, [isLoadingAllViews, isLoadingView, viewData]);

  const decompressedSpec = useDecompressedMultiRunWorkspaceSpec(
    entityName,
    projectName,
    parsedSpec
  );

  return useMemo(() => {
    if (!decompressedSpec) {
      return [];
    }
    try {
      return getPanelsBySectionForViewspec(decompressedSpec);
    } catch (e) {
      return [];
    }
  }, [decompressedSpec]);
}
